<script setup>
console.log('🔥 AI Literacy Principal Component Script 로드됨');

import { ref, onMounted, provide, nextTick, shallowRef, computed } from 'vue';
import { useRoute } from 'vue-router';
import { downloadResultPDF } from '@/utils/pdfDownloader.js';
import api from '@/lib/api.js';
import PageLayout from '@/components/result/ai-literacy/PageLayout.vue';
import Page1 from './Page1.vue';
import Page2 from './Page2.vue';
import Page3 from './Page3.vue';
import Page4 from './Page4.vue';
import Page5 from './Page5.vue';

console.log('📦 모든 imports 완료');

const route = useRoute();

console.log('🌍 setup 함수 실행 중, 현재 라우트:', route.path, route.query);

// AI 리터러시 결과 데이터
const principalData = ref(null);
const loading = ref(true);
const pdfDownloading = ref(false);
const downloadingDots = ref('.');
let dotsInterval = null;

// 페이지 구성을 동적으로 생성
const pages = computed(() => {
  const basePages = [
    { name: 'Page1', component: Page1, type: 'single' },
    { name: 'Page2', component: Page2, type: 'single' }
  ];

  // classList가 있으면 3반씩 그룹화하여 Page3 생성
  if (principalData.value && principalData.value.classList) {
    const classList = principalData.value.classList;
    const classGroups = [];
    
    // 3반씩 그룹화
    for (let i = 0; i < classList.length; i += 3) {
      classGroups.push(classList.slice(i, i + 3));
    }
    
    // 각 그룹마다 Page3 생성
    classGroups.forEach((classGroup, index) => {
      basePages.push({
        name: `Page3_${index + 1}`,
        component: Page3,
        type: 'class',
        data: classGroup,
        pageIndex: index
      });
    });
  }

  // Page4 추가
  basePages.push({ name: 'Page4', component: Page4, type: 'single' });

  basePages.push({ name: 'Page5', component: Page5, type: 'single' });

  return basePages;
});

// API 호출 함수
const fetchPrincipalResult = async () => {
  try {
    console.log('🚀 fetchPrincipalResult 시작');
    loading.value = true;
    const examId = route.query.exam_id;
    const schulCode = route.query.schul_code;
    
    console.log('📍 라우터 쿼리:', { examId, schulCode, fullQuery: route.query });
    
    if (!examId || !schulCode) {
      console.error('❌ exam_id 또는 schul_code가 없습니다:', route.query);
      throw new Error('exam_id와 schul_code가 필요합니다.');
    }

    // API 요청 파라미터 구성
    const requestData = {
      exam_id: examId,
      schul_code: schulCode
    };

    console.log('📤 AI 리터러시 교장 결과 API 요청:', requestData);

    const response = await api.post('/exam/tea/resultLitSchoolDetail', requestData);
    
    console.log('📥 API 응답:', response.data);

    if (response.data.success) {
      // nextTick을 사용하여 데이터 업데이트를 더 안전하게 처리
      await nextTick();
      principalData.value = response.data.result;
      console.log('✅ AI 리터러시 교장 결과 데이터 설정 완료:', principalData.value);
    } else {
      console.error('❌ API 응답 실패:', response.data);
      throw new Error(response.data.message || '데이터를 가져오는데 실패했습니다.');
    }
  } catch (error) {
    console.error('💥 AI 리터러시 교장 결과 데이터 조회 실패:', error);
    console.error('💥 에러 상세:', {
      message: error.message,
      response: error.response?.data,
      status: error.response?.status
    });
  } finally {
    loading.value = false;
    console.log('🏁 fetchPrincipalResult 완료, loading:', loading.value, 'principalData:', !!principalData.value);
  }
};

// 하위 컴포넌트에 데이터 전달
provide('principalData', principalData);
provide('loading', loading);

// 학교 전체 데이터 추출 (classList의 첫 번째 항목에서 공통 데이터 추출)
const schoolData = computed(() => {
  if (!principalData.value || !principalData.value.classList || principalData.value.classList.length === 0) {
    return null;
  }
  
  const firstClass = principalData.value.classList[0];
  return {
    exam_id: firstClass.exam_id,
    schul_code: firstClass.schul_code,
    schul_nm: firstClass.schul_nm,
    grade_code: firstClass.grade_code,
    schul_type: firstClass.schul_type,
    field1_score: firstClass.field1_score,
    field2_score: firstClass.field2_score,
    field3_score: firstClass.field3_score,
    field4_score: firstClass.field4_score,
    total_field1_rate: firstClass.total_field1_rate,
    total_field2_rate: firstClass.total_field2_rate,
    total_field3_rate: firstClass.total_field3_rate,
    total_field4_rate: firstClass.total_field4_rate
  };
});

provide('schoolData', schoolData);

const subTitle = computed(() => {
  if (schoolData.value && schoolData.value.schul_nm) {
    return schoolData.value.schul_nm + " 결과지";
  }
  return "학교 결과지";
});

// PDF 다운로드 함수
const triggerReportDownload = async () => {
  console.log('AI 리터러시 교장 PDF 다운로드 시작');
  
  try {
    // 로딩 상태 시작
        // PDF 다운로드 로딩 상태 시작
    pdfDownloading.value = true;
    
    // 점 애니메이션 시작
    dotsInterval = setInterval(() => {
      if (downloadingDots.value === '.') {
        downloadingDots.value = '..';
      } else if (downloadingDots.value === '..') {
        downloadingDots.value = '...';
      } else {
        downloadingDots.value = '.';
      }
    }, 500);

    console.log('PDF 생성 시작...');
    
    // 파일명 생성
    let filename = '';
    if (principalData.value && principalData.value.classList && principalData.value.classList.length > 0) {
      const schoolName = principalData.value.classList[0].schul_nm || '학교';
      const now = new Date();
      const dateStr = now.toISOString().slice(0, 10).replace(/-/g, '');
      filename = `${schoolName}_학교장용_AI리터러시_검사결과_${dateStr}.pdf`;
    } else {
      const now = new Date();
      const dateStr = now.toISOString().slice(0, 10).replace(/-/g, '');
      filename = `학교장용_AI리터러시_검사결과지_${dateStr}.pdf`;
    }
    
    // PDF 생성 진행률 콜백
    const onProgress = (progress) => {
      console.log('PDF 생성 진행률:', progress + '%');
    };
    
    // PDF 생성 및 다운로드
    const result = await downloadResultPDF({
      filename: filename,
      onProgress: onProgress,
      containerSelector: '.pdf-container',
      pageSelector: '.page-layout',
      // 화질 향상 옵션들
      scale: 2, // 해상도 2배 증가
      format: 'A4',
      printBackground: true,
      preferCSSPageSize: true,
      displayHeaderFooter: false,
      margin: {
        top: '0',
        right: '0',
        bottom: '0',
        left: '0'
      },
      // 고화질 이미지 렌더링
      quality: 100,
      dpi: 300 // 고해상도 DPI 설정
    });
    
    console.log('PDF 다운로드 완료:', result);
    
  } catch (error) {
    console.error('PDF 다운로드 실패:', error);
    alert('PDF 생성 중 오류가 발생했습니다. 다시 시도해주세요.');
  } finally {
    // PDF 다운로드 로딩 상태 종료
    pdfDownloading.value = false;
    
    // 점 애니메이션 중지
    if (dotsInterval) {
      clearInterval(dotsInterval);
      dotsInterval = null;
    }
    downloadingDots.value = '.';
  }
};

// 컴포넌트 마운트 시 데이터 로드
onMounted(() => {
  console.log('🎯 onMounted 호출됨');
  console.log('🔍 현재 라우트:', route.path, route.query);
  fetchPrincipalResult();
});
</script>

<template>
  <div class="top-area pt-5">
    <div class="loading">                 
        <div class="loading-overlay">
            <div class="loading-content">
                <div class="top-text pb-4">
                    <h4>검사결과지 다운로드 ({{pages.length}}페이지)</h4> 
                    <p class="sub-message pt-2">학교 전체의 리터러시 척도점수와 영역별 도달도 등 평균적인 분석 결과와 대시보드를 제공합니다.</p>    
                </div>                    
                  <button @click="triggerReportDownload" class="download-button" :disabled="pdfDownloading">
                    <span v-if="pdfDownloading">📥 다운로드 중{{ downloadingDots }}</span>
                    <span v-else>검사결과지 PDF 다운로드</span>
                </button>
            </div>
        </div>  
    </div>
  </div> 

  <!-- 로딩 중일 때 -->
  <div v-if="loading" class="text-center p-10">
    <p>📡 데이터를 불러오는 중...</p>
    <p class="text-sm text-gray-500 mt-2">exam_id: {{ route.query.exam_id }}</p>
    <p class="text-sm text-gray-500">schul_code: {{ route.query.schul_code }}</p>
  </div>

  <!-- 데이터 로드 완료 후 페이지 렌더링 -->
  <div v-else-if="principalData && !loading" class="pdf-container">
    <template v-for="(pageItem, index) in pages" :key="`page-${pageItem.name}-${index}`">
      <PageLayout 
        class="page-layout"
        :current-page="index + 1"
        :total-pages="pages.length"
        :sub-title="subTitle">
        <KeepAlive>
          <component 
            :is="pageItem.component" 
            :principalData="principalData"
            :schoolData="schoolData"
            :classData="pageItem.data"
            :pageIndex="pageItem.pageIndex"
            :key="`${pageItem.name}-${principalData.classList?.[0]?.exam_id || 'default'}`"
          />
        </KeepAlive>
      </PageLayout>
    </template>
  </div>

  <!-- 데이터 로드 실패 시 -->
  <div v-else class="text-center p-10">
    <p>❌ 데이터를 불러올 수 없습니다.</p>
    <p class="text-sm text-gray-500 mt-2">exam_id: {{ route.query.exam_id }}</p>
    <p class="text-sm text-gray-500">schul_code: {{ route.query.schul_code }}</p>
  </div>
</template>

<style lang="scss" scoped>
.top-area {
    width: 1130px ;
    margin: 0 auto;   
    position: relative;
    z-index: 20;
}

.loading {
    .loading-overlay {
        width: 1130px ;        
        padding: 25px 0 30px;
        display: flex;
        flex-direction: column;
        align-items: center;
        text-align: center;
        background-color: #fff;             
        border-radius: 15px; 
        font-size: 18px;        
    }
}

.top-text {
    font-weight: 500;
    line-height: 1.4;
}

.download-button {
    color: #fff;
    background-color: #247CFF;
    border-radius: 5px;
    padding: 4px 16px;
    cursor: pointer;
    border: none;
    
    &:disabled {
        background-color: #ccc;
        cursor: not-allowed;
        opacity: 0.7;
    }
}
</style>